import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";

function Formular() {
    const { register, handleSubmit, setValue, formState: { errors } } = useForm();
    const [searchParams] = useSearchParams();
    const sigRef = useRef();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState("");
    const [customerAddressQuery, setCustomerAddressQuery] = useState("");
    const [insuranceAddressQuery, setInsuranceAddressQuery] = useState("");
    const [customerAddressSuggestions, setCustomerAddressSuggestions] = useState([]);
    const [insuranceAddressSuggestions, setInsuranceAddressSuggestions] = useState([]);
    const requiredRule = (label) => ({ required: `${label} ist erforderlich` });
    const projectId = searchParams.get("projectId") || "";

    const getStreetFromAddress = (address) => {
        const road = address?.road || address?.pedestrian || address?.footway || address?.residential || "";
        const houseNumber = address?.house_number || "";
        return `${road} ${houseNumber}`.trim();
    };

    const getCityFromAddress = (address) => {
        return address?.city || address?.town || address?.village || address?.hamlet || address?.municipality || "";
    };

    const searchAddress = async (query, setSuggestions) => {
        if (!query || query.trim().length < 3) {
            setSuggestions([]);
            return;
        }

        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=de&limit=5&q=${encodeURIComponent(query.trim())}`
            );
            const results = await response.json();
            setSuggestions(Array.isArray(results) ? results : []);
        } catch (error) {
            setSuggestions([]);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            searchAddress(customerAddressQuery, setCustomerAddressSuggestions);
        }, 350);

        return () => clearTimeout(timer);
    }, [customerAddressQuery]);

    useEffect(() => {
        const timer = setTimeout(() => {
            searchAddress(insuranceAddressQuery, setInsuranceAddressSuggestions);
        }, 350);

        return () => clearTimeout(timer);
    }, [insuranceAddressQuery]);

    const applyAddressSelection = (scope, suggestion) => {
        const address = suggestion?.address || {};
        const street = getStreetFromAddress(address);
        const postalCode = address?.postcode || "";
        const city = getCityFromAddress(address);

        if (scope === "customer") {
            setValue("customerStreet", street, { shouldValidate: true });
            setValue("customerPostalCode", postalCode, { shouldValidate: true });
            setValue("customerCity", city, { shouldValidate: true });
            setCustomerAddressQuery(street);
            setCustomerAddressSuggestions([]);
            return;
        }

        setValue("insuranceStreet", street, { shouldValidate: true });
        setValue("insurancePostalCode", postalCode, { shouldValidate: true });
        setValue("insuranceCity", city, { shouldValidate: true });
        setInsuranceAddressQuery(street);
        setInsuranceAddressSuggestions([]);
    };

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        setSubmitMessage("");

        try {
            // Check if signature canvas is available and has content
            if (!sigRef.current) {
                throw new Error("Unterschrift-Canvas nicht verfügbar");
            }

            if (sigRef.current.isEmpty()) {
                throw new Error("Bitte unterschreiben Sie das Formular.");
            }

            const canvas = sigRef.current.getCanvas();
            const signature = canvas.toDataURL("image/png");
            if (!projectId) {
                throw new Error("Projektbezug fehlt. Bitte verwenden Sie den Link aus der E-Mail.");
            }

            const payload = { ...data, signature, projectId };

            console.log("Vertragsdaten:", payload);

            // Send to backend
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/insurance-form`, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            setSubmitMessage(response.data);
            alert("Formular erfolgreich abgeschickt! Sie erhalten eine E-Mail mit der Bestätigung.");

            // Reset form
            sigRef.current.clear();
        } catch (error) {
            console.error("Error submitting form:", error);
            const errorMessage = error.response?.data || "Fehler beim Absenden des Formulars. Bitte versuchen Sie es erneut.";
            setSubmitMessage(errorMessage);
            alert(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const clearSignature = () => {
        sigRef.current.clear();
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className="max-w-6xl mx-auto bg-gradient-to-br from-blue-50 to-white p-10 rounded-2xl shadow-2xl border border-blue-100"
        >
            {!projectId && (
                <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    Projektzuordnung fehlt. Bitte öffnen Sie das Formular über den Link aus der E-Mail.
                </div>
            )}

            {/* Anschriften */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-blue-100 rounded-xl p-5 shadow-inner">
                    <h3 className="font-bold mb-2 text-blue-700 text-lg">Unternehmen</h3>
                    <p className="text-gray-700 leading-relaxed">
                        Glas Lilienthal
                        <br />
                        Glaserei und Glasbau
                        <br />
                        M. Behrens & A. Kaus Gbr
                        <br />
                        Hauptstraße 53
                        <br />
                        28865 Lilienthal
                    </p>
                </div>
                <div>
                    <h3 className="font-bold mb-2 text-blue-700 text-lg">Kunde</h3>
                    <div className="space-y-3">
                        <input
                            {...register("customerName", requiredRule("Versicherungsnehmer"))}
                            className="w-full border border-blue-200 p-3 rounded-lg focus:ring-2 focus:ring-blue-300 transition"
                            placeholder="Versicherungsnehmer"
                            required
                        />
                        {errors.customerName && <p className="text-sm text-red-600">{errors.customerName.message}</p>}
                        <input
                            {...register("customerStreet", {
                                ...requiredRule("Straße"),
                                onChange: (event) => setCustomerAddressQuery(event.target.value)
                            })}
                            className="w-full border border-blue-200 p-3 rounded-lg focus:ring-2 focus:ring-blue-300 transition"
                            placeholder="Straße"
                            required
                            autoComplete="off"
                        />
                        {customerAddressSuggestions.length > 0 && (
                            <div className="border border-blue-200 rounded-lg bg-white shadow-md max-h-52 overflow-auto">
                                {customerAddressSuggestions.map((suggestion) => {
                                    const street = getStreetFromAddress(suggestion.address);
                                    const postalCode = suggestion.address?.postcode || "";
                                    const city = getCityFromAddress(suggestion.address);
                                    const key = `${suggestion.place_id}-${suggestion.lat}-${suggestion.lon}`;

                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-blue-100 last:border-b-0"
                                            onClick={() => applyAddressSelection("customer", suggestion)}
                                        >
                                            <span className="block text-gray-800">{street}</span>
                                            <span className="block text-sm text-gray-500">{postalCode} {city}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {errors.customerStreet && <p className="text-sm text-red-600">{errors.customerStreet.message}</p>}
                        <div className="flex space-x-4">
                            <input
                                {...register("customerPostalCode", requiredRule("PLZ"))}
                                className="w-full border border-blue-200 p-3 rounded-lg focus:ring-2 focus:ring-blue-300 transition"
                                placeholder="PLZ"
                                required
                            />
                            <input
                                {...register("customerCity", requiredRule("Ort"))}
                                className="w-full border border-blue-200 p-3 rounded-lg focus:ring-2 focus:ring-blue-300 transition"
                                placeholder="Ort"
                                required
                            />
                        </div>
                        {(errors.customerPostalCode || errors.customerCity) && (
                            <p className="text-sm text-red-600">PLZ und Ort sind erforderlich</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Vertragstext */}
            <p className="mb-6 text-gray-800 bg-blue-50 rounded-lg p-4 border-l-4 border-blue-300 shadow-sm">
                Sehr geehrter Kunde,<br /><br /> um Ihnen die Abwicklung des Schadens zu erleichtern,
                können wir auf Wunsch die weitere Abwicklung direkt mit Ihrer Versicherung
                vornehmen.
                <br /><br />
                Hierfür senden Sie uns diese Erklärung bitte vollständig ausgefüllt
                und unterzeichnet zurück:
            </p>

            <div className="space-y-5 mb-8">
                <label className="block font-medium text-gray-700">
                    Versicherungsname:
                    <input
                        {...register("insuranceName", requiredRule("Versicherungsname"))}
                        className="w-full border border-blue-200 p-3 rounded-lg mt-1 focus:ring-2 focus:ring-blue-300 transition"
                        placeholder="z.B. Allianz"
                        required
                    />
                    {errors.insuranceName && <p className="text-sm text-red-600 mt-1">{errors.insuranceName.message}</p>}
                </label>
                <label className="block font-medium text-gray-700">
                    Versicherungsanschrift:
                    <div className="space-y-3">
                        <input
                            {...register("insuranceAddressName", requiredRule("Name der Versicherung"))}
                            className="w-full border border-blue-200 p-3 rounded-lg focus:ring-2 focus:ring-blue-300 transition"
                            placeholder="Name"
                            required
                        />
                        {errors.insuranceAddressName && <p className="text-sm text-red-600">{errors.insuranceAddressName.message}</p>}
                        <input
                            {...register("insuranceStreet", {
                                ...requiredRule("Versicherungsstraße"),
                                onChange: (event) => setInsuranceAddressQuery(event.target.value)
                            })}
                            className="w-full border border-blue-200 p-3 rounded-lg focus:ring-2 focus:ring-blue-300 transition"
                            placeholder="Straße"
                            required
                            autoComplete="off"
                        />
                        {insuranceAddressSuggestions.length > 0 && (
                            <div className="border border-blue-200 rounded-lg bg-white shadow-md max-h-52 overflow-auto">
                                {insuranceAddressSuggestions.map((suggestion) => {
                                    const street = getStreetFromAddress(suggestion.address);
                                    const postalCode = suggestion.address?.postcode || "";
                                    const city = getCityFromAddress(suggestion.address);
                                    const key = `${suggestion.place_id}-${suggestion.lat}-${suggestion.lon}`;

                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-blue-100 last:border-b-0"
                                            onClick={() => applyAddressSelection("insurance", suggestion)}
                                        >
                                            <span className="block text-gray-800">{street}</span>
                                            <span className="block text-sm text-gray-500">{postalCode} {city}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {errors.insuranceStreet && <p className="text-sm text-red-600">{errors.insuranceStreet.message}</p>}
                        <div className="flex space-x-4">
                            <input
                                {...register("insurancePostalCode", requiredRule("Versicherungs-PLZ"))}
                                className="w-full border border-blue-200 p-3 rounded-lg focus:ring-2 focus:ring-blue-300 transition"
                                placeholder="PLZ"
                                required
                            />
                            <input
                                {...register("insuranceCity", requiredRule("Versicherungsort"))}
                                className="w-full border border-blue-200 p-3 rounded-lg focus:ring-2 focus:ring-blue-300 transition"
                                placeholder="Ort"
                                required
                            />
                        </div>
                        {(errors.insurancePostalCode || errors.insuranceCity) && (
                            <p className="text-sm text-red-600">PLZ und Ort der Versicherung sind erforderlich</p>
                        )}
                    </div>
                </label>
                <label className="block font-medium text-gray-700">
                    <span className="flex items-center gap-2">
                        Schadens-Nr. (WICHTIG!):
                        <span className="relative group inline-flex items-center">
                            <span className="w-5 h-5 rounded-full border border-blue-400 text-blue-600 text-xs font-bold flex items-center justify-center cursor-help">i</span>
                            <span className="absolute left-6 top-1/2 -translate-y-1/2 w-80 bg-white border border-blue-200 text-gray-700 text-sm font-normal rounded-lg shadow-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition">
                                Bitte melden Sie den Schaden zuerst Ihrer Versicherung. Sie erhalten danach eine Schadennummer. Das geht meist per App, Kontaktformular oder telefonisch bei Ihrer Versicherung.
                            </span>
                        </span>
                    </span>
                    <input
                        {...register("damageNumber", requiredRule("Schadensnummer"))}
                        className="w-full border border-blue-200 p-3 rounded-lg mt-1 focus:ring-2 focus:ring-blue-300 transition"
                        placeholder="Schadensnummer"
                        required
                    />
                    {errors.damageNumber && <p className="text-sm text-red-600 mt-1">{errors.damageNumber.message}</p>}
                </label>
                <label className="block font-medium text-gray-700">
                    Kundennummer bei der Versicherung (WICHTIG!):
                    <input
                        {...register("insuranceCustomerNumber", requiredRule("Versicherungs-Kundennummer"))}
                        className="w-full border border-blue-200 p-3 rounded-lg mt-1 focus:ring-2 focus:ring-blue-300 transition"
                        placeholder="Kundennummer"
                        required
                    />
                    {errors.insuranceCustomerNumber && <p className="text-sm text-red-600 mt-1">{errors.insuranceCustomerNumber.message}</p>}
                </label>
            </div>

            <p className="mb-8 text-gray-700 bg-blue-50 rounded-lg p-4 border-l-4 border-blue-300 shadow-sm">
                Als Auftraggeber trete(n) ich/wir, die mit mir/uns aus dem zuvor näher bezeichneten
                Versicherungsvertrag wegen des
            </p>

            <div className="space-y-5 mb-8">
                <label className="block font-medium text-gray-700">
                    Schadensereignis:
                    <input
                        {...register("damageEvent", requiredRule("Schadensereignis"))}
                        className="w-full border border-blue-200 p-3 rounded-lg mt-1 focus:ring-2 focus:ring-blue-300 transition"
                        placeholder="z.B. Glasbruch"
                        required
                    />
                    {errors.damageEvent && <p className="text-sm text-red-600 mt-1">{errors.damageEvent.message}</p>}
                </label>

                <label className="block font-medium text-gray-700">
                    Schadensdatum:
                    <input
                        type="date"
                        {...register("damageDate", requiredRule("Schadensdatum"))}
                        className="w-full border border-blue-200 p-3 rounded-lg mt-1 focus:ring-2 focus:ring-blue-300 transition"
                        required
                    />
                    {errors.damageDate && <p className="text-sm text-red-600 mt-1">{errors.damageDate.message}</p>}
                </label>
            </div>
            <p className="mb-8 text-gray-700 bg-blue-50 rounded-lg p-4 border-l-4 border-blue-300 shadow-sm">
                gegen die oben genannte Versicherung
                zustehenden Ansprüche in Höhe der werkvertraglichen Vergütungsanforderung der Firma
                „Glas Lilienthal“ unwiderruflich ab.
                <br /><br />
                Ich/Wir ermächtige(n) die Versicherung, gegen Vorlage
                der Rechnung die Zahlung direkt an die Firma „Glas Lilienthal“ zu leisten.
                <br /><br />
                Die Abtretung
                erfolgt erfüllungshalber. Es ist mir/uns bekannt, dass ich zur vollständigen Zahlung
                der Rechnung der Firma „Glas Lilienthal“ verpflichtet bin/sind, falls die Versicherung keine
                oder nur teilweise Zahlung leistet.
            </p>

            {/* Ort / Datum & Unterschrift */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
                <div>
                    <label className="block font-medium text-gray-700">
                        Ort / Datum:
                        <input
                            {...register("placeDate", requiredRule("Ort / Datum"))}
                            defaultValue={new Date().toLocaleDateString("de-DE")}
                            className="w-full border border-blue-200 p-3 rounded-lg mt-1 focus:ring-2 focus:ring-blue-300 transition"
                            placeholder="Ort, Datum"
                            required
                        />
                        {errors.placeDate && <p className="text-sm text-red-600 mt-1">{errors.placeDate.message}</p>}
                    </label>
                </div>
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-700">Unterschrift:</span>
                        <button
                            type="button"
                            onClick={clearSignature}
                            className="text-sm text-blue-600 hover:underline"
                        >
                            Löschen
                        </button>
                    </div>
                    <div className="bg-white border-2 border-blue-200 rounded-lg shadow-inner">
                        <SignatureCanvas
                            ref={sigRef}
                            canvasProps={{
                                width: 500,
                                height: 128,
                                className: "w-full h-32 rounded-lg cursor-crosshair"
                            }}
                        />
                    </div>
                </div>
            </div>


            <div className="mt-10 flex justify-end">
                <input
                    {...register("customerEmail", {
                        required: "E-Mail ist erforderlich",
                        pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Ungültige E-Mail-Adresse"
                        }
                    })}
                    type="email"
                    className="w-full border border-blue-200 p-3 rounded-lg mr-4 focus:ring-2 focus:ring-blue-300 transition"
                    placeholder="E-Mail-Adresse"
                    required
                />
                {errors.customerEmail && <p className="text-sm text-red-600 mr-4 mt-2">{errors.customerEmail.message}</p>}
                <button
                    type="submit"
                    className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:from-blue-700 hover:to-blue-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "Wird gesendet..." : "Absenden"}
                </button>
            </div>

            {submitMessage && (
                <div className="mt-4 p-4 rounded-lg bg-blue-50 border border-blue-200 text-blue-800">
                    {submitMessage}
                </div>
            )}
        </form>
    );
}

export default Formular;